import * as React from 'react';
import { Row } from 'react-bootstrap';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm, Field, destroy } from 'redux-form';

import { ReportBlockType } from '@acceligentllc/shared/enums/reportBlockType';

import { EDIT_BLOCK_NAME } from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import Checkbox from 'af-fields/Checkbox';
import Input from 'af-fields/Input';

import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';

import { validate } from './validation';
import type { ReportBlockFormModel } from '../../Shared/formModel';

export type CalculatedBlockNameForm = { name: string; isVisibleToCustomer: boolean; };

interface OwnProps {
	close: () => void;
	getInitialValues: () => ReportBlockFormModel;
	onSave: (form: CalculatedBlockNameForm, reportTypeBlockId: string) => void;
	reportTypeBlockId: Nullable<string>;
	show: boolean;
}

type Props = OwnProps & InjectedFormProps<CalculatedBlockNameForm, OwnProps>;

const REPORT_BLOCK_TYPES_WITH_EDITABLE_VISIBILITY = {
	[ReportBlockType.JOB_EHS]: true,
};

const _showVisibilityCheckbox = (type: Nullable<ReportBlockType>) => {
	return type && !!REPORT_BLOCK_TYPES_WITH_EDITABLE_VISIBILITY[type];
};

const EditBlockNameModal: React.FC<Props> = (props: Props) => {
	const { show, close, invalid, onSave, handleSubmit, getInitialValues, initialize, reportTypeBlockId } = props;

	const [reportBlockType, setReportBlockType] = React.useState<Nullable<ReportBlockType>>(null);

	React.useEffect(() => {
		if (show) {
			const values = getInitialValues();
			setReportBlockType(values.type);
			initialize({ name: values.name, isVisibleToCustomer: values.isVisibleToCustomer ?? true });
		} else {
			destroy();
		}
	}, [show, getInitialValues, initialize]);

	const handleSave = React.useCallback((form: CalculatedBlockNameForm) => {
		if (!reportTypeBlockId) {
			throw new Error('Missing block reportTypeBlockId');
		}
		onSave(form, reportTypeBlockId);
		close();
	}, [onSave, close, reportTypeBlockId]);

	return (
		<CustomModal
			className="report-block-modal"
			closeModal={close}
			modalStyle="info"
			showModal={show}
			size="sm"
		>
			<CustomModal.Header
				closeModal={close}
				title="Edit Segment"
			/>
			<CustomModal.Body>
				<Row className="row--non-padded">
					<Field
						component={Input}
						label="Segment Name"
						name="name"
					/>
				</Row>
				<Row className="row--non-padded">
					{_showVisibilityCheckbox(reportBlockType) &&
						<Field
							component={Checkbox}
							label="Is Visible To Customer"
							name="isVisibleToCustomer"
						/>
					}
				</Row>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={close}
					style="secondary"
				/>
				<SubmitButton
					disabled={invalid}
					label="Save"
					onClick={handleSubmit(handleSave)}
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	reduxForm<CalculatedBlockNameForm, OwnProps>({ form: EDIT_BLOCK_NAME, validate })
);

export default enhance(EditBlockNameModal);
